import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, Phone, ClipboardList, Globe, ChevronDown, Mic, Volume2, VolumeX, Wind } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import axios from "axios";
import RegistrationForm from "@/components/RegistrationForm";
import { StorageForm } from "@/components/StorageForm";
import { DepositForm } from "@/components/DepositForm";
import { LivePanel } from "@/components/LivePanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Alessia avatar
const ALESSIA_AVATAR = "https://static.prod-images.emergentagent.com/jobs/7a3bde6e-45ab-4e1a-afed-e103645fa64a/images/44d12b6cd07429b675a4375f2b8813a9156472c564502c1d325fb2931bb1547d.png";
const ALESSIA_AVATAR_LARGE = "https://static.prod-images.emergentagent.com/jobs/7a3bde6e-45ab-4e1a-afed-e103645fa64a/images/44d12b6cd07429b675a4375f2b8813a9156472c564502c1d325fb2931bb1547d.png";

const generateId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Country to language mapping
const countryToLanguage = {
  // Italian
  'IT': 'it',
  // English
  'GB': 'en', 'US': 'en', 'AU': 'en', 'CA': 'en', 'NZ': 'en', 'IE': 'en',
  // French
  'FR': 'fr', 'BE': 'fr', 'CH': 'fr', 'MC': 'fr', 'LU': 'fr',
  // German
  'DE': 'de', 'AT': 'de', 'LI': 'de',
  // Spanish
  'ES': 'es', 'MX': 'es', 'AR': 'es', 'CO': 'es', 'CL': 'es', 'PE': 'es', 'VE': 'es'
};

// Detect language from IP geolocation
const detectLanguageFromIP = async () => {
  try {
    // Use free IP geolocation API
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    
    if (data && data.country_code) {
      const detectedLang = countryToLanguage[data.country_code] || 'en';
      console.log(`🌍 Detected country: ${data.country_code} (${data.country_name}), language: ${detectedLang}`);
      return detectedLang;
    }
  } catch (error) {
    console.log('IP detection failed, using default language');
  }
  return 'it'; // Default to Italian
};

// Language configurations
const languages = {
  it: {
    code: "it",
    name: "Italiano",
    flag: "🇮🇹",
    greeting: "Ciao! 👋 Sono Alessia di Porto Pollo Academy! Come posso aiutarti?",
    welcomeBack: "Ciao! 👋 Bentornato! Come posso aiutarti?",
    callToAction: "Vieni a fare sport con noi! 🌊",
    placeholder: "Scrivi un messaggio...",
    whatsapp: "Contattaci",
    quickReplies: ["Quanto costa?", "Orari lezioni", "Voglio prenotare", "Condizioni meteo", "Dove siete?"],
    formButton: "📋 Compila il Modulo di Iscrizione",
    storageButton: "Modulo Noleggio",
    errorMessage: "Ops! C'è stato un problema 😅 Contattaci: WhatsApp +39 351 364 4743",
    timeoutMessage: "Mi scuso, sto avendo un piccolo problema tecnico. Riprova tra qualche secondo! Per info urgenti: WhatsApp +39 351 364 4743",
    bookingHint: "Tesseramento / Prenotazione",
  },
  en: {
    code: "en",
    name: "English",
    flag: "🇬🇧",
    greeting: "Hi! 👋 I'm Alessia from Porto Pollo Academy! How can I help you?",
    welcomeBack: "Hi! 👋 Welcome back! How can I help you?",
    callToAction: "Come ride with us! 🌊",
    placeholder: "Write a message...",
    whatsapp: "Contact us",
    quickReplies: ["How much?", "Lesson times", "I want to book", "Weather conditions", "Where are you?"],
    formButton: "📋 Fill Registration Form",
    storageButton: "Rental Form",
    errorMessage: "Oops! Something went wrong 😅 Contact us: WhatsApp +39 351 364 4743",
    timeoutMessage: "Sorry, I'm having a small technical issue. Try again in a few seconds! For urgent info: WhatsApp +39 351 364 4743",
    bookingHint: "Membership / Booking",
  },
  fr: {
    code: "fr",
    name: "Français",
    flag: "🇫🇷",
    greeting: "Salut! 👋 Je suis Alessia de Porto Pollo Academy! Comment puis-je vous aider?",
    welcomeBack: "Salut! 👋 Bon retour! Comment puis-je vous aider?",
    callToAction: "Venez rider avec nous! 🌊",
    placeholder: "Écrivez un message...",
    whatsapp: "Contactez-nous",
    quickReplies: ["Combien ça coûte?", "Horaires des cours", "Je veux réserver", "Conditions météo", "Où êtes-vous?"],
    formButton: "📋 Remplir le formulaire d'inscription",
    storageButton: "Formulaire Location",
    errorMessage: "Oups! Un problème est survenu 😅 Contactez-nous: WhatsApp +39 351 364 4743",
    timeoutMessage: "Désolée, j'ai un petit problème technique. Réessayez dans quelques secondes ! Pour info urgentes: WhatsApp +39 351 364 4743",
    bookingHint: "Inscription / Réservation",
  },
  de: {
    code: "de",
    name: "Deutsch",
    flag: "🇩🇪",
    greeting: "Hallo! 👋 Ich bin Alessia von Porto Pollo Academy! Wie kann ich Ihnen helfen?",
    welcomeBack: "Hallo! 👋 Willkommen zurück! Wie kann ich Ihnen helfen?",
    callToAction: "Komm reiten mit uns! 🌊",
    placeholder: "Nachricht schreiben...",
    whatsapp: "Kontakt",
    quickReplies: ["Wie viel kostet es?", "Unterrichtszeiten", "Ich möchte buchen", "Wetterbedingungen", "Wo sind Sie?"],
    formButton: "📋 Anmeldeformular ausfüllen",
    storageButton: "Mietformular",
    errorMessage: "Ups! Etwas ist schief gelaufen 😅 Kontaktieren Sie uns: WhatsApp +39 351 364 4743",
    timeoutMessage: "Entschuldigung, ich habe ein kleines technisches Problem. Versuchen Sie es in ein paar Sekunden erneut! Für dringende Infos: WhatsApp +39 351 364 4743",
    bookingHint: "Anmeldung / Buchung",
  },
  es: {
    code: "es",
    name: "Español",
    flag: "🇪🇸",
    greeting: "¡Hola! 👋 Soy Alessia de Porto Pollo Academy! ¿Cómo puedo ayudarte?",
    welcomeBack: "¡Hola! 👋 ¡Bienvenido de nuevo! ¿Cómo puedo ayudarte?",
    callToAction: "¡Ven a hacer deporte con nosotros! 🌊",
    placeholder: "Escribe un mensaje...",
    whatsapp: "Contáctanos",
    quickReplies: ["¿Cuánto cuesta?", "Horarios de clases", "Quiero reservar", "Condiciones del tiempo", "¿Dónde están?"],
    formButton: "📋 Completar formulario de inscripción",
    storageButton: "Formulario Alquiler",
    errorMessage: "¡Ups! Algo salió mal 😅 Contáctanos: WhatsApp +39 351 364 4743",
    timeoutMessage: "Lo siento, tengo un pequeño problema técnico. ¡Inténtalo de nuevo en unos segundos! Para info urgente: WhatsApp +39 351 364 4743",
    bookingHint: "Inscripción / Reserva",
  }
};

export default function AgneseChatPage() {
  const [language, setLanguage] = useState("it");
  const [languageDetected, setLanguageDetected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showStorageForm, setShowStorageForm] = useState(false);
  const [showDepositForm, setShowDepositForm] = useState(false);
  const [showLivePanel, setShowLivePanel] = useState(false);
  const [agneseState, setAgneseState] = useState("waving");
  const [hasInteracted, setHasInteracted] = useState(false);
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem("alessia_session_id");
    if (stored) return stored;
    const newId = generateId();
    localStorage.setItem("alessia_session_id", newId);
    return newId;
  });
  
  // Voice states
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentAudioRef = useRef(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [inputFocused, setInputFocused] = useState(false);
  const [showBookingHint, setShowBookingHint] = useState(true);
  const [showWingHint, setShowWingHint] = useState(false);
  const lang = languages[language];

  // Timed hints - booking hint for 10s, then wing hint for 10s
  useEffect(() => {
    // Hide booking hint after 10 seconds
    const bookingTimer = setTimeout(() => {
      setShowBookingHint(false);
      setShowWingHint(true);
    }, 10000);
    
    // Hide wing hint after 20 seconds total (10+10)
    const wingTimer = setTimeout(() => {
      setShowWingHint(false);
    }, 20000);
    
    return () => {
      clearTimeout(bookingTimer);
      clearTimeout(wingTimer);
    };
  }, []);

  // Auto-detect language from IP on first load
  useEffect(() => {
    const initLanguage = async () => {
      // Check if user MANUALLY set a language (not auto-detected)
      const manualLang = localStorage.getItem("alessia_language_manual");
      if (manualLang) {
        setLanguage(manualLang);
        setLanguageDetected(true);
        return;
      }
      
      // Auto-detect from IP every time
      const detectedLang = await detectLanguageFromIP();
      setLanguage(detectedLang);
      setLanguageDetected(true);
    };
    initLanguage();
  }, []);

  // Initialize greeting message
  useEffect(() => {
    if (languageDetected) {
      setMessages([{ role: "assistant", content: lang.greeting }]);
    }
  }, [language, languageDetected]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasInteracted) {
        setAgneseState("normal");
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [hasInteracted]);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const response = await axios.get(`${API}/chat/history/${sessionId}`);
        if (response.data && response.data.length > 0) {
          setMessages([
            { role: "assistant", content: lang.welcomeBack },
            ...response.data.map(msg => ({ role: msg.role, content: msg.content }))
          ]);
          setHasInteracted(true);
          setAgneseState("smiling");
        }
      } catch (error) {
        console.log("No previous chat history");
      }
    };
    loadHistory();
  }, [sessionId]);

  const changeLanguage = (newLang) => {
    setLanguage(newLang);
    // Save as MANUAL preference (overrides auto-detection)
    localStorage.setItem("alessia_language_manual", newLang);
    triggerSmile();
    
    // Show warning for French and German about instructor availability
    if (newLang === "fr" || newLang === "de") {
      const warningMsg = newLang === "fr" 
        ? "⚠️ Note: Pour les cours en français, la disponibilité d'un instructeur francophone n'est pas garantie. Veuillez confirmer: WhatsApp +39 351 364 4743"
        : "⚠️ Hinweis: Für Unterricht auf Deutsch ist die Verfügbarkeit eines deutschsprachigen Lehrers nicht garantiert. Bitte bestätigen Sie: WhatsApp +39 351 364 4743";
      
      setTimeout(() => {
        setMessages(prev => [...prev, { role: "assistant", content: warningMsg }]);
      }, 500);
    }
  };

  const triggerSmile = () => {
    if (!hasInteracted) {
      setHasInteracted(true);
    }
    setAgneseState("smiling");
    setTimeout(() => setAgneseState("normal"), 2000);
  };

  const handleSend = async (messageText = null) => {
    const textToSend = messageText || input.trim();
    if (!textToSend || isLoading) return;

    triggerSmile();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: textToSend }]);
    setIsLoading(true);

    // Add language instruction to the message
    const messageWithLang = `[Rispondi in ${lang.name}] ${textToSend}`;

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: messageWithLang
      }, { timeout: 18000 });

      const aiResponse = response.data.response;
      
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: aiResponse }
      ]);

      // Auto-play TTS if enabled
      if (ttsEnabled && aiResponse) {
        playTTS(aiResponse);
      }

      if (response.data.show_form) {
        setTimeout(() => {
          setMessages(prev => [
            ...prev,
            { role: "system", content: "form_button" }
          ]);
        }, 500);
      }

      // Show rental button in chat
      if (response.data.show_rental) {
        setTimeout(() => {
          setMessages(prev => [
            ...prev,
            { role: "system", content: "rental_button" }
          ]);
        }, 500);
      }

      // Show deposit button in chat
      if (response.data.show_deposit) {
        setTimeout(() => {
          setMessages(prev => [
            ...prev,
            { role: "system", content: "deposit_button" }
          ]);
        }, 500);
      }
    } catch (error) {
      console.error("Chat error:", error);
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.response?.status === 524;
      const isNetwork = error.message === 'Network Error' || !error.response;
      const msg = (isTimeout || isNetwork) ? lang.timeoutMessage : lang.errorMessage;
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: msg }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Text-to-Speech function
  const playTTS = async (text) => {
    try {
      // Stop any current audio
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      
      setIsSpeaking(true);
      setAgneseState("speaking");
      
      const response = await axios.post(`${API}/tts`, {
        text: text,
        voice: "nova" // Friendly female voice for Alessia
      });
      
      // Create audio from base64
      const audioBlob = new Blob(
        [Uint8Array.from(atob(response.data.audio), c => c.charCodeAt(0))],
        { type: 'audio/mp3' }
      );
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;
      
      audio.onended = () => {
        setIsSpeaking(false);
        setAgneseState("smiling");
        setTimeout(() => setAgneseState("normal"), 1000);
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.onerror = () => {
        setIsSpeaking(false);
        setAgneseState("normal");
      };
      
      await audio.play();
    } catch (error) {
      console.error("TTS error:", error);
      setIsSpeaking(false);
      setAgneseState("normal");
    }
  };
  
  // Stop TTS
  const stopTTS = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    setIsSpeaking(false);
    setAgneseState("normal");
  };

  // Speech-to-Text functions - Hold to talk like WhatsApp
  const recordingRef = useRef(false);
  
  const startRecording = async (e) => {
    if (e) e.preventDefault();
    if (recordingRef.current || isLoading) return;
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        if (audioBlob.size < 1000) {
          console.log("Recording too short, ignoring");
          return;
        }
        
        try {
          const formData = new FormData();
          formData.append('audio', audioBlob, 'recording.webm');
          
          const response = await axios.post(`${API}/stt`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          if (response.data.text && response.data.text.trim()) {
            handleSend(response.data.text.trim());
          }
        } catch (error) {
          console.error("STT error:", error);
        }
      };
      
      mediaRecorder.start();
      recordingRef.current = true;
      setIsRecording(true);
      triggerSmile();
    } catch (error) {
      console.error("Microphone access error:", error);
    }
  };
  
  const stopRecording = (e) => {
    if (e) e.preventDefault();
    if (mediaRecorderRef.current && recordingRef.current) {
      mediaRecorderRef.current.stop();
      recordingRef.current = false;
      setIsRecording(false);
    }
  };

  const toggleRecording = (e) => {
    if (e) e.preventDefault();
    if (recordingRef.current) {
      stopRecording(e);
    } else {
      startRecording(e);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickReplyClick = (text) => {
    triggerSmile();
    handleSend(text);
  };

  const avatarVariants = {
    waving: {
      rotate: [0, -10, 10, -10, 10, 0],
      scale: [1, 1.05, 1.05, 1.05, 1.05, 1],
      transition: { duration: 1.5, repeat: Infinity, repeatDelay: 1 }
    },
    speaking: {
      scale: [1, 1.05, 1, 1.05, 1],
      transition: { duration: 0.8, repeat: Infinity }
    },
    smiling: {
      scale: [1, 1.1, 1.05],
      transition: { duration: 0.5 }
    },
    normal: {
      scale: 1,
      rotate: 0
    }
  };

  return (
    <div 
      className="h-screen w-full flex flex-col overflow-hidden bg-gradient-to-br from-teal-400 via-cyan-500 to-blue-500" 
      data-testid="agnese-chat"
      onClick={() => !hasInteracted && triggerSmile()}
    >
      
      {/* Language Selector - Top Left */}
      <div className="absolute top-4 left-4 z-50">
        <div className="flex flex-col items-start gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white rounded-full px-3 py-2 h-auto"
                data-testid="language-selector"
              >
                <Globe className="w-4 h-4 mr-2" />
                <span className="text-lg mr-1">{lang.flag}</span>
                <span className="text-sm font-medium">{lang.name}</span>
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              {Object.values(languages).map((l) => (
                <DropdownMenuItem 
                  key={l.code}
                  onClick={() => changeLanguage(l.code)}
                  className={`cursor-pointer ${language === l.code ? 'bg-teal-50' : ''}`}
                  data-testid={`lang-${l.code}`}
                >
                  <span className="text-lg mr-2">{l.flag}</span>
                  <span>{l.name}</span>
                  {language === l.code && <span className="ml-auto text-teal-600">✓</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Arrow hint for language change - below the button */}
          <motion.div 
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="flex items-center text-white/80 text-xs ml-2"
          >
            <span className="mr-1">↑</span>
            <span>change language</span>
          </motion.div>
        </div>
      </div>

      {/* Header con foto grande di Alessia */}
      <motion.div 
        className="relative flex-shrink-0"
        animate={{ 
          height: inputFocused ? "auto" : "auto",
          paddingTop: inputFocused ? "1rem" : "2rem",
          paddingBottom: inputFocused ? "0.5rem" : "1.5rem"
        }}
        transition={{ duration: 0.3 }}
      >
        <div 
          className="absolute inset-0 bg-cover bg-center blur-2xl opacity-40"
          style={{ backgroundImage: `url(${ALESSIA_AVATAR_LARGE})` }}
        />
        
        <div className="relative px-4 text-center">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ 
              scale: inputFocused ? 0.6 : 1, 
              opacity: 1 
            }}
            transition={{ duration: 0.3 }}
            className="relative inline-block"
          >
            <motion.div 
              className="relative mx-auto rounded-full overflow-hidden border-4 border-white shadow-2xl"
              style={{ 
                width: inputFocused ? "4rem" : "9rem",
                height: inputFocused ? "4rem" : "9rem"
              }}
              variants={avatarVariants}
              animate={agneseState}
            >
              <img src={ALESSIA_AVATAR_LARGE} alt="Alessia" className="w-full h-full object-cover object-top" />
            </motion.div>
            
            {!inputFocused && (
              <motion.div 
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute top-2 right-2 w-6 h-6 bg-green-400 rounded-full border-3 border-white shadow-lg"
              />
            )}
            
            <AnimatePresence>
              {agneseState === "waving" && (
                <motion.div
                  initial={{ opacity: 0, scale: 0, x: 20, y: -20 }}
                  animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                  exit={{ opacity: 0, scale: 0 }}
                  className="absolute -top-2 -right-2 text-3xl"
                >
                  <motion.span
                    animate={{ rotate: [0, 20, -20, 20, -20, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                  >
                    👋
                  </motion.span>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {agneseState === "smiling" && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0 }}
                  className="absolute -top-2 -right-2 text-2xl"
                >
                  😊
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {agneseState === "speaking" && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0 }}
                  className="absolute -top-2 -right-2 text-2xl"
                >
                  <motion.span
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                  >
                    🗣️
                  </motion.span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: inputFocused ? 0 : 1, height: inputFocused ? 0 : "auto" }}
            transition={{ delay: 0.3 }}
            className={inputFocused ? "overflow-hidden" : ""}
          >
            <h1 className={`mt-2 font-bold text-white drop-shadow-lg ${inputFocused ? "text-lg" : "text-3xl md:text-4xl"}`}>Alessia</h1>
            <p className="text-white/90 text-sm md:text-base mt-1">Porto Pollo Academy 🏄‍♀️</p>
            
            {/* Pulsante Vuoi prenotare? - visible for first 5 seconds */}
            <AnimatePresence>
              {showBookingHint && (
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  onClick={() => setShowForm(true)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="mt-3 px-4 py-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white rounded-full text-sm font-medium border border-white/30 transition-all"
                  data-testid="quick-booking-btn"
                >
                  {lang.bookingHint}
                </motion.button>
              )}
            </AnimatePresence>
            
            <AnimatePresence>
              {agneseState === "waving" && !hasInteracted && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-2 text-white/80 text-sm"
                >
                  {lang.callToAction}
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </motion.div>

      {/* Chat Container */}
      <motion.div 
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, type: "spring", damping: 20 }}
        className="flex-1 bg-white rounded-t-[2.5rem] shadow-2xl flex flex-col overflow-hidden"
      >
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 py-5 space-y-3" data-testid="chat-messages">
          <AnimatePresence>
            {messages.map((msg, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.25 }}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "system" && msg.content === "form_button" ? (
                  <motion.button
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => { triggerSmile(); setShowForm(true); }}
                    className="flex items-center gap-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white px-5 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all"
                    data-testid="open-form-btn"
                  >
                    <ClipboardList className="w-5 h-5" />
                    {lang.formButton}
                  </motion.button>
                ) : msg.role === "system" && msg.content === "rental_button" ? (
                  <motion.button
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => { triggerSmile(); setShowStorageForm(true); }}
                    className="flex items-center gap-2 bg-gradient-to-r from-sky-400 to-cyan-500 text-white px-5 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all"
                    data-testid="open-rental-btn"
                  >
                    RENTAL
                  </motion.button>
                ) : msg.role === "system" && msg.content === "deposit_button" ? (
                  <motion.button
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => { triggerSmile(); setShowDepositForm(true); }}
                    className="flex items-center gap-2 bg-gradient-to-r from-emerald-400 to-teal-500 text-white px-5 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all"
                    data-testid="open-deposit-btn"
                  >
                    DEPOSITO
                  </motion.button>
                ) : msg.role !== "system" && (
                  <>
                    {msg.role === "assistant" && (
                      <div className="w-9 h-9 rounded-full overflow-hidden mr-2 flex-shrink-0 border-2 border-teal-200 shadow-md">
                        <img src={ALESSIA_AVATAR} alt="Alessia" className="w-full h-full object-cover object-top" />
                      </div>
                    )}
                    <div
                      className={`max-w-[78%] px-4 py-3 rounded-2xl shadow-sm ${
                        msg.role === "user"
                          ? "bg-gradient-to-br from-teal-500 to-cyan-600 text-white rounded-br-md"
                          : "bg-gray-100 text-gray-800 rounded-bl-md"
                      }`}
                    >
                      <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
          
          {isLoading && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-teal-200 shadow-md">
                <img src={ALESSIA_AVATAR} alt="Alessia" className="w-full h-full object-cover object-top" />
              </div>
              <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Replies */}
        {messages.length <= 2 && !hasInteracted && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="px-4 pb-2"
          >
            <div className="flex flex-wrap gap-2 justify-center">
              {lang.quickReplies.map((text, i) => (
                <button
                  key={i}
                  onClick={() => handleQuickReplyClick(text)}
                  className="bg-gradient-to-r from-teal-50 to-cyan-50 hover:from-teal-100 hover:to-cyan-100 text-teal-700 text-sm px-4 py-2 rounded-full transition-all border border-teal-200 shadow-sm hover:shadow-md active:scale-95"
                  data-testid={`quick-reply-${i}`}
                >
                  {text}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Input Area */}
        <div className="p-3 pb-4 border-t border-gray-100 bg-white/95 backdrop-blur-sm safe-area-bottom">
          <div className="max-w-lg mx-auto">
            {/* Input row - full width */}
            <div className="flex gap-2 items-center">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                onFocus={() => { triggerSmile(); setInputFocused(true); }}
                onBlur={() => setInputFocused(false)}
                placeholder={lang.placeholder}
                className="flex-1 rounded-full border-gray-200 bg-gray-50 focus:bg-white focus:border-teal-400 focus:ring-teal-400 h-12 px-5 text-base"
                disabled={isLoading || isRecording}
                data-testid="chat-input"
              />
              <Button
                onClick={() => handleSend()}
                disabled={!input.trim() || isLoading}
                className="rounded-full w-12 h-12 p-0 shrink-0 bg-gradient-to-br from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 shadow-lg hover:shadow-xl transition-all active:scale-95"
                data-testid="send-btn"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </Button>
            </div>
            {/* Icons row - below input */}
            <div className="flex items-center justify-center gap-2 mt-2">
              <Button
                onClick={() => setShowForm(true)}
                variant="ghost"
                className="rounded-full w-10 h-10 p-0 text-gray-400 hover:text-teal-600 hover:bg-gray-100"
                data-testid="attach-btn"
                title={lang.formButton}
              >
                <ClipboardList className="w-5 h-5" />
              </Button>
              <Button
                onClick={() => setShowStorageForm(true)}
                variant="ghost"
                className="rounded-full h-8 px-3 text-[10px] font-bold uppercase tracking-wider text-gray-400 hover:text-sky-600 hover:bg-sky-50"
                data-testid="storage-form-btn"
                title={lang.storageButton}
              >
                RENTAL
              </Button>
              <Button
                onClick={() => setShowDepositForm(true)}
                variant="ghost"
                className="rounded-full h-8 px-3 text-[10px] font-bold uppercase tracking-wider text-gray-400 hover:text-emerald-600 hover:bg-emerald-50"
                data-testid="deposit-form-btn"
                title="Deposito"
              >
                DEPOSITO
              </Button>
              <Button
                onClick={() => setShowLivePanel(true)}
                variant="ghost"
                className="rounded-full w-10 h-10 p-0 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50"
                data-testid="live-panel-btn"
                title="Live Webcam & Wind"
              >
                <Wind className="w-5 h-5" />
              </Button>
              <Button
                onClick={() => {
                  if (isSpeaking) {
                    stopTTS();
                  } else {
                    setTtsEnabled(!ttsEnabled);
                  }
                }}
                variant="ghost"
                className={`rounded-full w-10 h-10 p-0 transition-all ${
                  isSpeaking 
                    ? "text-orange-500 animate-pulse" 
                    : ttsEnabled 
                      ? "text-teal-500 hover:text-teal-600 hover:bg-teal-50" 
                      : "text-gray-400 hover:text-teal-600 hover:bg-gray-100"
                }`}
                data-testid="tts-toggle"
              >
                {isSpeaking || ttsEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </Button>
              <Button
                onClick={toggleRecording}
                variant="ghost"
                className={`rounded-full w-10 h-10 p-0 select-none transition-all ${
                  isRecording 
                    ? "text-red-500 animate-pulse scale-110 bg-red-50" 
                    : "text-gray-400 hover:text-teal-600 hover:bg-gray-100"
                }`}
                disabled={isLoading}
                data-testid="mic-btn"
              >
                <Mic className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Registration Form Modal */}
      <RegistrationForm 
        isOpen={showForm} 
        onClose={() => setShowForm(false)} 
        sessionId={sessionId}
        language={language}
      />

      {/* Storage/Rental Form Modal */}
      <StorageForm 
        isOpen={showStorageForm} 
        onClose={() => setShowStorageForm(false)} 
        sessionId={sessionId}
        language={language}
      />

      {/* Deposit Form Modal */}
      <DepositForm 
        isOpen={showDepositForm} 
        onClose={() => setShowDepositForm(false)} 
        sessionId={sessionId}
        language={language}
      />

      {/* Live Webcam & Wind Panel */}
      <LivePanel 
        isOpen={showLivePanel} 
        onClose={() => setShowLivePanel(false)} 
        language={language}
      />
    </div>
  );
}
